import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';
import SEO from '../components/SEO';
import './StaticPages.css';

export default function Terms() {
  const navigate = useNavigate();

  return (
    <div className="static-page">
      <SEO 
        title="Terms of Service - RazChatz | User Agreement"
        description="Read RazChatz's terms of service. Understand your rights, responsibilities, and our policies for using the platform safely and legally."
        keywords="terms of service, user agreement, terms and conditions, acceptable use policy, legal terms, razchatz terms, service agreement"
        canonical="https://razchatz.netlify.app/terms"
        ogTitle="RazChatz Terms of Service - User Agreement"
        ogDescription="Complete terms of service for RazChatz users. Learn about acceptable use, user rights, and platform policies."
      />
      <div className="static-page-header">
        <button className="back-button" onClick={() => navigate('/')}>
          <ArrowLeft size={20} />
          Back to Home
        </button>
      </div>

      <div className="static-page-container">
        <h1 className="static-page-title">Terms of Service</h1>
        <p className="last-updated">Last Updated: December 13, 2025</p>
        
        <div className="static-page-content">
          <section className="policy-intro">
            <p className="lead-text">
              Welcome to RazChatz! These Terms of Service ("Terms") govern your access to and use of 
              RazChatz's services, including our website, mobile applications, and any related services 
              (collectively, the "Service"). By using RazChatz, you agree to these Terms.
            </p>
          </section>

          <section className="policy-section">
            <div className="policy-icon-wrapper">
              <FileText className="policy-icon" />
            </div>
            <h2>1. Acceptance of Terms</h2>
            <p>
              By accessing or using RazChatz, you agree to be bound by these Terms and our Privacy Policy. 
              If you do not agree to these Terms, you may not use our Service. We reserve the right to 
              modify these Terms at any time, and your continued use constitutes acceptance of any changes.
            </p>
          </section>

          <section className="policy-section">
            <h2>2. Eligibility</h2>
            <p>
              You must be at least 13 years old to use RazChatz. By using our Service, you represent 
              and warrant that:
            </p>
            <ul>
              <li>You are at least 13 years of age</li>
              <li>You have the legal capacity to enter into these Terms</li>
              <li>You will comply with all applicable laws and regulations</li>
              <li>All information you provide is accurate and current</li>
            </ul>
          </section>

          <section className="policy-section">
            <h2>3. Account Registration</h2>
            <h3>Creating an Account</h3>
            <p>
              To access certain features, you must create an account. You agree to:
            </p>
            <ul>
              <li>Provide accurate, complete, and current information</li>
              <li>Maintain the security of your password</li>
              <li>Notify us immediately of any unauthorized access</li>
              <li>Be responsible for all activities under your account</li>
            </ul>

            <h3>Guest Access</h3>
            <p>
              Guest chatting is available with limited features. Guest sessions are temporary and 
              data may not be retained after your session ends.
            </p>
          </section>

          <section className="policy-section">
            <div className="policy-icon-wrapper">
              <CheckCircle className="policy-icon" />
            </div>
            <h2>4. Acceptable Use</h2>
            <p>You agree to use RazChatz only for lawful purposes. You agree NOT to:</p>
            <ul>
              <li>Violate any laws or regulations</li>
              <li>Infringe on intellectual property rights</li>
              <li>Transmit harmful, offensive, or inappropriate content</li>
              <li>Harass, threaten, or intimidate other users</li>
              <li>Impersonate any person or entity</li>
              <li>Distribute spam, malware, or viruses</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Interfere with or disrupt the Service</li>
              <li>Use automated systems (bots) without permission</li>
              <li>Collect user information without consent</li>
            </ul>
          </section>

          <section className="policy-section">
            <div className="policy-icon-wrapper">
              <XCircle className="policy-icon" />
            </div>
            <h2>5. Prohibited Content</h2>
            <p>You may not post, share, or transmit content that:</p>
            <ul>
              <li>Is illegal, harmful, or promotes illegal activities</li>
              <li>Contains hate speech, discrimination, or violence</li>
              <li>Is sexually explicit or pornographic</li>
              <li>Infringes on copyrights, trademarks, or other rights</li>
              <li>Contains personal information of others without consent</li>
              <li>Is false, misleading, or fraudulent</li>
              <li>Promotes self-harm or dangerous activities</li>
            </ul>
          </section>

          <section className="policy-section">
            <h2>6. User Content</h2>
            <h3>Your Content</h3>
            <p>
              You retain ownership of content you post on RazChatz. By posting content, you grant us 
              a worldwide, non-exclusive, royalty-free license to use, reproduce, and display your 
              content solely for operating and improving the Service.
            </p>

            <h3>Content Moderation</h3>
            <p>
              We reserve the right to review, monitor, and remove content that violates these Terms. 
              However, we are not obligated to monitor all content and are not responsible for user-generated content.
            </p>
          </section>

          <section className="policy-section">
            <h2>7. Intellectual Property</h2>
            <p>
              RazChatz and its original content, features, and functionality are owned by Mohammad Razak A 
              and are protected by international copyright, trademark, and other intellectual property laws.
            </p>
            <p>You may not:</p>
            <ul>
              <li>Copy, modify, or distribute our content without permission</li>
              <li>Reverse engineer or decompile our software</li>
              <li>Remove copyright or proprietary notices</li>
              <li>Use our trademarks without authorization</li>
            </ul>
          </section>

          <section className="policy-section">
            <h2>8. Privacy and Data Protection</h2>
            <p>
              Your privacy is important to us. Our collection and use of personal information is governed 
              by our Privacy Policy. By using RazChatz, you consent to our data practices as described 
              in the Privacy Policy.
            </p>
          </section>

          <section className="policy-section">
            <h2>9. Termination</h2>
            <h3>By You</h3>
            <p>
              You may terminate your account at any time by contacting us or using account deletion 
              features in the app.
            </p>

            <h3>By Us</h3>
            <p>
              We may suspend or terminate your account immediately, without notice, if you:
            </p>
            <ul>
              <li>Violate these Terms</li>
              <li>Engage in fraudulent or illegal activities</li>
              <li>Pose a security risk to the Service or other users</li>
              <li>Fail to comply with applicable laws</li>
            </ul>
            <p>
              Upon termination, your right to use the Service ceases immediately. We may delete your 
              account and content, though some information may be retained as required by law.
            </p>
          </section>

          <section className="policy-section">
            <div className="policy-icon-wrapper">
              <AlertTriangle className="policy-icon" />
            </div>
            <h2>10. Disclaimers</h2>
            <p>
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, 
              EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO:
            </p>
            <ul>
              <li>Warranties of merchantability or fitness for a particular purpose</li>
              <li>Warranties of non-infringement</li>
              <li>Warranties regarding accuracy, reliability, or availability</li>
              <li>Warranties that the Service will be uninterrupted or error-free</li>
            </ul>
            <p>
              We do not guarantee that the Service will meet your requirements or that defects will 
              be corrected. Use of the Service is at your own risk.
            </p>
          </section>

          <section className="policy-section">
            <h2>11. Limitation of Liability</h2>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, RAZCHATZ AND ITS AFFILIATES SHALL NOT BE LIABLE FOR:
            </p>
            <ul>
              <li>Indirect, incidental, special, or consequential damages</li>
              <li>Loss of profits, data, or goodwill</li>
              <li>Service interruptions or errors</li>
              <li>User content or conduct</li>
              <li>Third-party actions or content</li>
            </ul>
            <p>
              Our total liability shall not exceed the amount you paid us in the past 12 months, 
              or $100, whichever is greater.
            </p>
          </section>

          <section className="policy-section">
            <h2>12. Indemnification</h2>
            <p>
              You agree to indemnify and hold harmless RazChatz, its affiliates, and their respective 
              officers, directors, employees, and agents from any claims, damages, losses, liabilities, 
              and expenses (including legal fees) arising from:
            </p>
            <ul>
              <li>Your use of the Service</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any rights of another party</li>
              <li>Your content posted on the Service</li>
            </ul>
          </section>

          <section className="policy-section">
            <h2>13. Third-Party Services</h2>
            <p>
              RazChatz may contain links to third-party websites or services. We are not responsible 
              for the content, privacy policies, or practices of third-party sites. Your use of 
              third-party services is at your own risk.
            </p>
          </section>

          <section className="policy-section">
            <h2>14. Dispute Resolution</h2>
            <h3>Informal Resolution</h3>
            <p>
              If you have a dispute, please contact us first at support@razchatz.com. We will attempt 
              to resolve disputes informally.
            </p>

            <h3>Governing Law</h3>
            <p>
              These Terms are governed by and construed in accordance with applicable laws, without 
              regard to conflict of law principles.
            </p>

            <h3>Arbitration</h3>
            <p>
              Any disputes that cannot be resolved informally shall be resolved through binding 
              arbitration, except where prohibited by law.
            </p>
          </section>

          <section className="policy-section">
            <h2>15. Changes to Terms</h2>
            <p>
              We reserve the right to modify these Terms at any time. We will notify users of material 
              changes via email or platform notification. Your continued use after changes constitutes 
              acceptance of the modified Terms.
            </p>
          </section>

          <section className="policy-section">
            <h2>16. General Provisions</h2>
            <ul>
              <li><strong>Entire Agreement:</strong> These Terms constitute the entire agreement between you and RazChatz</li>
              <li><strong>Severability:</strong> If any provision is found invalid, the remaining provisions remain in effect</li>
              <li><strong>Waiver:</strong> Failure to enforce any right does not waive that right</li>
              <li><strong>Assignment:</strong> You may not assign these Terms; we may assign them without restriction</li>
              <li><strong>No Agency:</strong> These Terms do not create any agency, partnership, or joint venture</li>
            </ul>
          </section>

          <section className="policy-section">
            <h2>17. Contact Information</h2>
            <p>
              For questions about these Terms, please contact us:
            </p>
            <ul className="contact-info">
              <li><strong>Email:</strong> legal@razchatz.com</li>
              <li><strong>Support:</strong> support@razchatz.com</li>
              <li><strong>Website:</strong> https://razchatz.netlify.app</li>
            </ul>
          </section>

          <section className="cta-section">
            <p>
              By using RazChatz, you acknowledge that you have read, understood, and agree to be 
              bound by these Terms of Service.
            </p>
            <div className="cta-buttons">
              <button className="cta-button primary" onClick={() => navigate('/signup')}>
                Accept & Get Started
              </button>
              <button className="cta-button secondary" onClick={() => navigate('/privacy')}>
                Read Privacy Policy
              </button>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
